import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	Card,
	Descriptions,
	Divider,
	Spin,
	Typography,
	Button,
	Modal,
	Form,
	message,
} from 'antd';
import { PlayCircleOutlined, SyncOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	getSingleBulkSender,
	distributeBulkSenderFund,
	distributeCronBulkSenderFund,
} from '../../methods/redux/reducers/bulkSender';
import BalanceItem from './user';
import { TableHeader } from '.';

const { Text } = Typography;

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;

const BulkSenderDetails = () => {
	const { id } = useParams<{ id: string }>();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isLoadingCreateJob, setIsLoadingCreateJob] = useState(false);

	const dispatch = useAppDispatch();

	const { singleBulkSender } = useAppSelector((store) => store.bulkSender);

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
	};

	const getBulkSender = async () => {
		setLoading(true);
		const res: any = await dispatch(getSingleBulkSender({ i: id }));
		if (res) {
			setLoading(false);
		}
	};

	useEffect(() => {
		getBulkSender();
	}, [id]);

	const handleSubmitCronJob = async () => {
		try {
			setIsLoadingCreateJob(true);

			await dispatch(
				distributeCronBulkSenderFund({
					bulkListID: singleBulkSender?.profile?._id,
				})
			);

			handleCancel();
		} catch (error) {
			message.error('Failed to create bulk sender job');
		} finally {
			setIsLoadingCreateJob(false);
		}
	};

	const handleSubmit = async () => {
		try {
			setIsLoadingCreateJob(true);

			await dispatch(
				distributeBulkSenderFund({ bulkListID: singleBulkSender?.profile?._id })
			);

			handleCancel();
		} catch (error) {
			message.error('Failed to create bulk sender job');
		} finally {
			setIsLoadingCreateJob(false);
		}
	};

	if (loading) {
		return <Spin size="large" />;
	}

	return (
		<Card title="Bulk Sender Details" style={{ margin: '20px' }}>
			<Descriptions bordered column={2}>
				<Descriptions.Item label="Title">
					<Text strong>{singleBulkSender?.profile?.title}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Currency">
					<Text strong>
						{singleBulkSender?.profile?.currency?.name} (
						{singleBulkSender?.profile?.currency?.symbol})
					</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Total Users">
					<Text strong>{singleBulkSender?.total_user}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Amount">
					<Text strong>
						{singleBulkSender?.total_amount?.[0]?.total_pay_amount}
					</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Funder">
					<Text strong>{singleBulkSender?.profile?.funder?.email}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Status">
					<Text
						style={{
							color:
								singleBulkSender?.profile?.status === 'active'
									? 'green'
									: 'red',
						}}
					>
						{singleBulkSender?.profile?.status}
					</Text>
				</Descriptions.Item>
			</Descriptions>

			<br />

			<p>
				Total of <strong>{singleBulkSender?.jobs?.length} items</strong> found
			</p>

			<Divider />

			{/* <Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotBalance?.paginator.itemCount}
				current={walletSnapshotBalance?.paginator.currentPage}
				pageSize={walletSnapshotBalance?.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/> */}

			<br />

			<div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
				<Button
					type="primary"
					icon={<PlayCircleOutlined />}
					onClick={() => {
						showModal();
					}}
				>
					Start Process
				</Button>
				<Button
					type="default"
					icon={<SyncOutlined />}
					disabled={singleBulkSender?.profile?.status !== 'processing'}
					onClick={() => {
						handleSubmitCronJob();
					}}
				>
					Run Cron Job
				</Button>
			</div>

			<TableHeader
				style={{
					gridTemplateColumns: ' 40px 1fr 1fr 1fr 100px 1fr 90px',
					gap: '4px',
				}}
			>
				<div>S/N</div>
				<div>Email</div>
				<div>Name</div>
				<div>Amount (USDT)</div>
				<div>Rate (Naira)</div>
				<div>Salary (Naira)</div>
				<div>Status</div>
			</TableHeader>

			{singleBulkSender?.jobs?.length > 0 &&
				singleBulkSender?.jobs?.map((item: any, index: number) => (
					<BalanceItem key={item._id} item={item} index={index + 1} />
				))}

			<br />

			<Divider />
			{/* <Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotBalance?.paginator.itemCount}
				current={walletSnapshotBalance?.paginator.currentPage}
				pageSize={walletSnapshotBalance?.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/> */}

			{/* Modal for Creating a Job */}
			<Modal
				title={`Send Total of ${singleBulkSender?.profile?.currency?.symbol} ${singleBulkSender?.total_amount?.[0]?.total_pay_amount}`}
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<p>
						Funds will be deducted from user:{' '}
						<strong>{singleBulkSender?.profile?.funder?.email}</strong> right
						away
					</p>
					<p>
						Wallet: get wallet of{' '}
						<strong>{singleBulkSender?.profile?.funder?.email}</strong>
					</p>
					<p>Fund the wallet above to credit this funder account</p>

					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={isLoadingCreateJob}
						>
							{isLoadingCreateJob ? 'Distributing...' : 'Distribute funds now'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Distribute funds later
						</Button>
					</Form.Item>

					<p>
						Funds will be distributed by a cron job within 1 hr (or you run it
						manually) and notify all users with the job title.
					</p>

					<Divider />
					<p>
						Sample message to user: You have received a total of 200.00 USDT
						from : <strong>Wicrypt Salary for may</strong>
					</p>
				</Form>
			</Modal>
		</Card>
	);
};

export default BulkSenderDetails;
